import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Pagination,
  Modal,
  Button,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { colors } from '@src/styles';
import { statusOptions, TTaskItem, TTaskSource, TTaskType } from '../hooks/dto/taskItem.dto';
import { TaskTable } from './TaskTable';
import { GroupsSkeleton } from './GroupsSkeleton';
import { StateMessage } from './StateMessage';
import { statusColors, statusColorsText, statusIcons, statusLabels } from './StatusBar';
import { useTasksContext, TaskGroup } from '../context/TasksContext';

const TaskGroupCard = ({ group }: { group: TaskGroup }) => {
  const { handleGroupSelect } = useTasksContext();
  const completionRate =
    (((group.statusCounts['CONCLUDED'] || 0) + (group.statusCounts['REJECTED'] || 0)) / group.totalTasks) * 100;

  return (
    <Card
      onClick={() => handleGroupSelect(group)}
      sx={{
        backgroundColor: colors.background.primary,
        border: `1px solid ${colors.background.secondary}`,
        borderRadius: '8px',
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: colors.primary.yellow,
          boxShadow: `0 4px 12px rgba(255, 160, 21, 0.1)`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: colors.primary.white, fontWeight: 600, mb: 1, fontSize: '18px' }}>
              {group.message}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                label={`${group.totalTasks} ${group.totalTasks !== 1 ? 'tarefas' : 'tarefa'}`}
                size="small"
                sx={{ backgroundColor: colors.primary.yellow, color: colors.background.primary, fontWeight: 600 }}
              />
              <Typography variant="body2" sx={{ color: colors.primary.yellow }}>
                {completionRate.toFixed(0)}% concluído
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: `${colors.primary.yellow}20`,
                '& .MuiLinearProgress-bar': { backgroundColor: colors.primary.yellow, borderRadius: 3 },
              }}
            />
          </Box>
          <IconButton
            sx={{ color: colors.primary.yellow, '&:hover': { backgroundColor: `${colors.primary.yellow}20` } }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(group.statusCounts).map(([status, count]) => {
            if (count === 0) return null;
            const Icon = statusIcons[status as keyof typeof statusIcons];
            return (
              <Chip
                key={status}
                icon={Icon ? <Icon sx={{ fontSize: '16px' }} /> : undefined}
                label={`${count} ${statusLabels[status] || status}`}
                size="small"
                sx={{
                  backgroundColor: `${statusColors[status]}20`,
                  color: statusColors[status],
                  border: `1px solid ${statusColors[status]}40`,
                  '& .MuiChip-icon': { color: statusColors[status] },
                }}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const GroupDetailView = ({ group }: { group: TaskGroup }) => {
  const { handleGroupSelect, handleBulkUpdateTasks, teamMembers } = useTasksContext();

  const [openGroupChangesModal, setOpenGroupChangesModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{
    status?: TTaskItem['status'];
    userId?: string | null;
  }>({});

  const mappedTasks = useMemo<TTaskItem[]>(
    () =>
      group.tasks.map((task) => ({
        id: task.id,
        createdAt: task.createdAt,
        completedAt: task.completedAt,
        completedByUserId: task.completedByUserId,
        assignedUserId: task.assignedUserId,
        status: task.status,
        message: group.message,
        source: group.source as TTaskSource,
        type: group.type as TTaskType,
        organizationId: group.organizationId,
      })),
    [group],
  );

  const currentBulkStatus = useMemo(() => {
    const uniqueStatuses = Array.from(new Set(mappedTasks.map((t) => t.status)));
    return uniqueStatuses.length === 1 && uniqueStatuses[0] ? uniqueStatuses[0] : null;
  }, [mappedTasks]);

  const currentAssignedUserId = useMemo(() => {
    const uniqueUsers = Array.from(new Set(mappedTasks.map((t) => t.assignedUserId)));
    return uniqueUsers.length === 1 && uniqueUsers[0] ? uniqueUsers[0] : null;
  }, [mappedTasks]);

  const effectiveBulkStatus = pendingChanges.status !== undefined ? pendingChanges.status : currentBulkStatus;
  const effectiveAssignedUserId = pendingChanges.userId !== undefined ? pendingChanges.userId : currentAssignedUserId;

  const completionRate =
    (((group.statusCounts['CONCLUDED'] || 0) + (group.statusCounts['REJECTED'] || 0)) / group.totalTasks) * 100;
  const taskIds = group.tasks.map((task) => task.id);
  const userList = useMemo(
    () => [
      { id: null as string | null, name: 'Não atribuído' },
      ...teamMembers.map((member) => ({ id: member.id as string | null, name: member.name })),
    ],
    [teamMembers],
  );

  const handleStatusChange = (status: TTaskItem['status']) => {
    setPendingChanges((prev) => ({ ...prev, status }));
  };

  const handleUserChange = (userId: string | null) => {
    setPendingChanges((prev) => ({ ...prev, userId }));
  };

  const handleSubmitChanges = () => {
    if (Object.keys(pendingChanges).length === 0) {
      setOpenGroupChangesModal(false);
      return;
    }

    const payload: {
      taskIds: string[];
      newStatus?: TTaskItem['status'];
      newAssignedUserId?: string | null;
    } = { taskIds };

    if (pendingChanges.status) {
      payload.newStatus = pendingChanges.status;
    }

    if (pendingChanges.userId !== undefined) {
      payload.newAssignedUserId = pendingChanges.userId;
    }

    handleBulkUpdateTasks(payload);
    setPendingChanges({});
    setOpenGroupChangesModal(false);
  };

  const handleModalClose = () => {
    setPendingChanges({});
    setOpenGroupChangesModal(false);
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;
  const selectedUser = userList.find((user) => user.id === effectiveAssignedUserId) || null;

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton
              onClick={() => handleGroupSelect(null)}
              sx={{ color: colors.primary.yellow, '&:hover': { backgroundColor: `${colors.primary.yellow}20` } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ color: colors.primary.white, fontWeight: 600, flex: 1 }}>
              {group.message || 'Sem nome'}
            </Typography>

            <Button
              variant="contained"
              onClick={() => setOpenGroupChangesModal(true)}
              sx={{
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: colors.primary.yellow, opacity: 0.9 },
              }}
            >
              Alterações em massa
            </Button>
          </Box>

          <Modal open={openGroupChangesModal} onClose={handleModalClose} aria-labelledby="group-changes-modal">
            <Box
              sx={{
                position: 'absolute' as const,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                maxHeight: '80vh',
                bgcolor: colors.background.primary,
                border: `2px solid ${colors.primary.yellow}`,
                borderRadius: '12px',
                boxShadow: `0 8px 32px rgba(255, 160, 21, 0.3)`,
                p: 4,
                overflow: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ color: colors.primary.white, fontWeight: 600 }}>
                  Alterações em massa
                </Typography>
                <IconButton
                  onClick={handleModalClose}
                  sx={{ color: colors.primary.white, '&:hover': { backgroundColor: colors.background.secondary } }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Typography variant="body2" sx={{ color: colors.primary.white, mb: 4, opacity: 0.7 }}>
                Fazer alterações em massa em todas as {group.totalTasks} tarefas deste grupo.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: colors.primary.white, fontWeight: 600, mb: 2 }}>
                  Alterar Status
                </Typography>
                <Divider sx={{ mb: 3, borderColor: colors.background.secondary }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                  {statusOptions.map((status) => {
                    const Icon = statusIcons[status as keyof typeof statusIcons];
                    return (
                      <Button
                        key={status}
                        fullWidth
                        variant={effectiveBulkStatus === status ? 'contained' : 'outlined'}
                        onClick={() => handleStatusChange(status)}
                        sx={{
                          py: 1.5,
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          color:
                            effectiveBulkStatus === status
                              ? statusColorsText[status] || colors.brand.ink
                              : statusColors[status] || colors.primary.white,
                          backgroundColor: effectiveBulkStatus === status ? statusColors[status] : 'transparent',
                          borderColor: statusColors[status] || colors.background.secondary,
                          '&:hover': {
                            backgroundColor: statusColors[status]
                              ? `${statusColors[status]}40`
                              : colors.background.secondary,
                            borderColor: statusColors[status] || colors.primary.yellow,
                          },
                        }}
                      >
                        <Box sx={{ mr: 1 }}>{Icon ? <Icon sx={{ fontSize: '16px' }} /> : null}</Box>
                        {statusLabels[status] || status}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ color: colors.primary.white, fontWeight: 600, mb: 2 }}>
                  Atribuir Usuário
                </Typography>
                <Divider sx={{ mb: 3, borderColor: colors.background.secondary }} />
                <Autocomplete
                  value={selectedUser}
                  onChange={(_, newValue) => handleUserChange(newValue?.id || null)}
                  options={userList}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar usuário..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: colors.primary.white,
                          backgroundColor: colors.background.secondary,
                          borderRadius: '8px',
                        },
                        '& .MuiInputBase-input': { color: colors.primary.white },
                        '& .MuiInputBase-input::placeholder': { color: colors.primary.white, opacity: 0.5 },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        py: 1.5,
                        px: 2,
                        backgroundColor: colors.background.primary,
                        color: colors.primary.white,
                        '&:hover': { backgroundColor: colors.background.secondary },
                        '&[aria-selected="true"]': {
                          backgroundColor: `${colors.primary.yellow}20`,
                          color: colors.primary.yellow,
                          fontWeight: 600,
                        },
                      }}
                    >
                      {option.id === null ? <em>{option.name}</em> : option.name}
                    </Box>
                  )}
                  sx={{
                    '& .MuiAutocomplete-paper': {
                      backgroundColor: colors.background.primary,
                      border: `1px solid ${colors.background.secondary}`,
                    },
                    '& .MuiAutocomplete-listbox': {
                      backgroundColor: colors.background.primary,
                      '& .MuiAutocomplete-option': { color: colors.primary.white },
                    },
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: colors.background.primary,
                        border: `1px solid ${colors.background.secondary}`,
                        borderRadius: '8px',
                        mt: 1,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleModalClose}
                  sx={{
                    color: colors.primary.white,
                    borderColor: colors.background.secondary,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: colors.primary.white, backgroundColor: colors.background.secondary },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitChanges}
                  disabled={!hasChanges}
                  sx={{
                    backgroundColor: colors.primary.yellow,
                    color: colors.background.primary,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 4,
                    '&:hover': { backgroundColor: colors.primary.yellow, opacity: 0.9 },
                    '&:disabled': {
                      backgroundColor: colors.background.secondary,
                      color: colors.primary.white,
                      opacity: 0.5,
                    },
                  }}
                >
                  Enviar Alterações
                </Button>
              </Box>
            </Box>
          </Modal>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, ml: 6 }}>
            <Chip
              label={`${group.totalTasks} ${group.totalTasks !== 1 ? 'tarefas' : 'tarefa'}`}
              size="small"
              sx={{ backgroundColor: colors.primary.yellow, color: colors.background.primary, fontWeight: 600 }}
            />
            <Typography variant="body2" sx={{ color: colors.primary.yellow }}>
              {completionRate.toFixed(0)}% concluído
            </Typography>
          </Box>

          <Box sx={{ ml: 6 }}>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: `${colors.primary.yellow}20`,
                '& .MuiLinearProgress-bar': { backgroundColor: colors.primary.yellow, borderRadius: 3 },
                width: '500px',
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, ml: 6 }}>
            {Object.entries(group.statusCounts).map(([status, count]) => {
              if (count === 0) return null;
              const Icon = statusIcons[status as keyof typeof statusIcons];
              return (
                <Chip
                  key={status}
                  icon={Icon ? <Icon sx={{ fontSize: '16px' }} /> : undefined}
                  label={`${count} ${statusLabels[status] || status}`}
                  size="small"
                  sx={{
                    backgroundColor: `${statusColors[status]}20`,
                    color: statusColors[status],
                    border: `1px solid ${statusColors[status]}40`,
                    '& .MuiChip-icon': { color: statusColors[status] },
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <TaskTable tasks={mappedTasks} />
      </Box>
    </Fade>
  );
};

export const GroupsView = () => {
  const {
    selectedGroup,
    taskGroups,
    isLoadingGroups,
    groupsError,
    retryGroups,
    groupsCurrentPage,
    groupsTotalPages,
    handleGroupsPageChange,
  } = useTasksContext();

  if (isLoadingGroups) {
    return <GroupsSkeleton cardCount={8} />;
  }

  if (selectedGroup) {
    return <GroupDetailView group={selectedGroup} />;
  }

  if (groupsError) {
    return (
      <StateMessage
        variant="error"
        title="Não foi possível carregar os grupos"
        description="A busca falhou. Verifique sua conexão e tente de novo."
        onRetry={retryGroups}
      />
    );
  }

  if (taskGroups.length === 0) {
    return (
      <StateMessage
        variant="empty"
        title="Nenhum grupo encontrado"
        description="Nenhum grupo corresponde aos filtros selecionados. Tente ampliar o período ou limpar os filtros."
      />
    );
  }

  return (
    <Box sx={{ height: 'calc(100%)', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 2,
        }}
      >
        {taskGroups.map((group) => (
          <TaskGroupCard key={`${group.message}-${group.organizationId}`} group={group} />
        ))}
      </Box>
      {groupsTotalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
            borderTop: `1px solid ${colors.background.secondary}`,
            backgroundColor: colors.background.primary,
          }}
        >
          <Pagination
            count={groupsTotalPages}
            page={groupsCurrentPage}
            onChange={(_, page) => handleGroupsPageChange(page)}
            color="primary"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                color: colors.primary.white,
                borderColor: colors.background.secondary,
                '&:hover': { backgroundColor: colors.background.secondary },
              },
              '& .Mui-selected': {
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
                '&:hover': { backgroundColor: colors.primary.yellow },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
